class LangSelector extends HHBaseElement {
  constructor() {
    super();

    this.countrySelector = this.querySelector(".js-country-selector")
    this.languageSelector = this.querySelector(".js-language-selector")
    this.countryCombo = this.querySelector('[data-is-country]')
    this.countryComboInput = this.countryCombo?.querySelector('[role="combobox"]')
    this.form = this.querySelector("form")
    this.langHiddenInput = this.querySelector(".js-lang")

    // Language the page is currently rendered in, captured before
    // toggleLangSelector() mutates the hidden input. Used to skip no-op reselects.
    this.currentLang = this.langHiddenInput?.value

    // Sync the language sub-options with the current country once on load. We do
    // not react to later `countryChanged` events: in the apply-on-select model a
    // real country change navigates away, and reacting would let a blur-committed
    // selection wrongly re-filter/hide the language list for the current market.
    this.toggleLangSelector()
    this.initializeLanguageCombobox()

    // Apply on select (no Apply button — matches the modal design). We bind to
    // explicit clicks / keyboard commits instead of the countryChanged event so a
    // blur (backdrop click, tab-away, opening the language list) can't commit and
    // redirect to a country the shopper never chose.
    this.bindCountryOptions()
    this.bindLanguageOptions()
  }

  bindCountryOptions() {
    if (!this.countrySelector) return

    this.countrySelector.querySelectorAll('.js-combo-option').forEach((option) => {
      // Defer so the dropdown's own click handler sets aria-selected first.
      option.addEventListener('click', () => setTimeout(() => this.applyCountry(), 0))
    })

    // Keyboard: Enter / Space commit the highlighted option while the menu is open.
    // Capture phase runs before the dropdown's own handler closes the menu, so we
    // can read the open state reliably.
    this.countryComboInput?.addEventListener(
      'keydown',
      (event) => {
        const isCommit = event.key === 'Enter' || event.key === ' '
        const menuOpen = this.countryCombo?.classList.contains('open')
        if (isCommit && menuOpen) {
          setTimeout(() => this.applyCountry(), 0)
        }
      },
      true
    )
  }

  // Apply immediately when the user picks a different country.
  applyCountry() {
    const option = this.countrySelector?.querySelector('[aria-selected="true"]')
    const iso = option?.dataset?.isoCode
    const current = window.currentCountryCode

    if (!iso) return
    if (current && iso.toUpperCase() === current.toUpperCase()) return

    // Resolve a language the destination market actually supports — the language
    // dropdown still reflects the previous country, so we can't reuse its value.
    this.submit(option, this.getLanguageForCountry(option))
  }

  // Keep the current language if the destination country supports it, otherwise
  // fall back to that country's default (first configured) language.
  getLanguageForCountry(option) {
    const languages = (option?.dataset?.languages || 'en')
      .split(',')
      .map((lang) => lang.trim().toLowerCase())
      .filter(Boolean)

    if (languages.length === 0) return 'en'

    if (this.currentLang && languages.includes(this.currentLang.toLowerCase())) {
      return this.currentLang.toLowerCase()
    }

    return languages[0]
  }

  // The option for the country the page is currently rendered in, regardless of
  // any selection the shared combobox may have committed on blur.
  getCurrentCountryOption() {
    if (!this.countrySelector || !window.currentCountryCode) return null

    const current = window.currentCountryCode.toUpperCase()
    return [...this.countrySelector.querySelectorAll('.js-combo-option')].find(
      (option) => (option.dataset.isoCode || '').toUpperCase() === current
    ) || null
  }

  // Apply immediately when the user picks a different language for the current country.
  bindLanguageOptions() {
    if (!this.languageSelector) return

    this.languageSelector.querySelectorAll('.js-combo-option').forEach((option) => {
      // Defer so the dropdown's own click handler sets aria-selected first.
      option.addEventListener('click', () => setTimeout(() => this.applyLanguage(), 0))
    })

    // Keyboard: mirror the country handler so Enter / Space commits also apply.
    const languageCombo = this.languageSelector.querySelector('.js-select')
    languageCombo?.querySelector('[role="combobox"]')?.addEventListener(
      'keydown',
      (event) => {
        const isCommit = event.key === 'Enter' || event.key === ' '
        const menuOpen = languageCombo.classList.contains('open')
        if (isCommit && menuOpen) {
          setTimeout(() => this.applyLanguage(), 0)
        }
      },
      true
    )
  }

  applyLanguage() {
    const lang = this.form?.querySelector('[aria-selected="true"]')?.dataset?.value
    if (!lang) return
    if (this.currentLang && lang.toLowerCase() === this.currentLang.toLowerCase()) return

    // A language change keeps the current country — never trust a country
    // selection a blur may have committed in the background.
    const countryOption = this.getCurrentCountryOption()
      || this.countrySelector?.querySelector('[aria-selected="true"]')
    this.submit(countryOption)
  }

  initializeLanguageCombobox() {
    const languageCombobox = this.languageSelector.querySelector('[role="combobox"]');
    const currentSelectedOption = this.languageSelector.querySelector('[aria-selected="true"]:not(.hidden)');

    if (languageCombobox && currentSelectedOption) {
      languageCombobox.innerHTML = currentSelectedOption.innerHTML;
    }
  }

  toggleLangSelector() {
    const countryOption = this.countrySelector.querySelector('[aria-selected="true"]');
    const countryIso = countryOption?.dataset?.isoCode;
    const languages = (countryOption?.dataset?.languages || 'en').split(',').map(lang => lang.trim().toLowerCase());

    this.languageSelector.querySelectorAll('[role="option"]').forEach(option => {
      const langValue = option.dataset.value.toLowerCase();
      if (languages.includes(langValue)) {
        option.classList.remove("hidden");
      } else {
        option.classList.add("hidden");
      }
    });

    // Always select the first available language for the new country
    const visibleOptions = this.languageSelector.querySelectorAll('[role="option"]:not(.hidden)');

    if (visibleOptions.length > 0) {
      // Remove current selection from all options
      this.languageSelector.querySelectorAll('[role="option"]').forEach(opt => {
        opt.setAttribute('aria-selected', 'false');
        opt.classList.remove('option-current');
      });

      // Set first visible option as selected
      visibleOptions[0].setAttribute('aria-selected', 'true');
      visibleOptions[0].classList.add('option-current');

      // Update the combobox display text to show the selected language
      const languageCombobox = this.languageSelector.querySelector('[role="combobox"]');
      if (languageCombobox) {
        languageCombobox.innerHTML = visibleOptions[0].innerHTML;
      }

      // Update hidden input
      if (this.langHiddenInput) {
        this.langHiddenInput.value = visibleOptions[0].dataset.value;
      }
    }

    if (countryIso === "US") {
      this.languageSelector.classList.add("hidden")
    } else {
      this.languageSelector.classList.remove("hidden")
    }
  }

  submit(countryOption, lang) {
    const option = countryOption || this.countrySelector?.querySelector('[aria-selected="true"]')
    const countryUrl = option?.dataset?.href
    if (!countryUrl) return

    const language = lang
      || this.form?.querySelector('[aria-selected="true"]')?.dataset.value
      || this.langHiddenInput?.value
      || 'en'

    window.location.href = `${countryUrl.replace('en-', `${language}-`)}`;
  }
}

window.customElements.define('lang-selector', LangSelector);
